<script setup lang="ts">
import { computed } from 'vue'
import { useOrgStore } from '@/stores/org'

interface Props {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  animated?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  animated: false,
})

const orgStore = useOrgStore()

const logoInfo = computed(() => orgStore.logoInfo)
const branding = computed(() => orgStore.branding)

const sizeClasses = computed(
  () =>
    ({
      sm: 'logo-sm',
      md: 'logo-md',
      lg: 'logo-lg',
      xl: 'logo-xl',
    })[props.size]
)

// Разбить текстовый логотип на буквы для анимации
const letters = computed(() => {
  if (logoInfo.value.type === 'text') {
    return logoInfo.value.value.split('')
  }
  return []
})
</script>

<template>
  <div class="org-logo" :class="[sizeClasses, { animated }]">
    <!-- Текстовый логотип -->
    <template v-if="logoInfo.type === 'text'">
      <span
        v-for="(letter, index) in letters"
        :key="index"
        class="logo-letter"
        :style="animated ? { animationDelay: `${index * 1}s` } : {}"
      >
        {{ letter }}
      </span>
    </template>

    <!-- Изображение логотипа -->
    <template v-else>
      <img
        :src="logoInfo.value"
        :alt="branding.appNameShort"
        class="logo-image"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.org-logo {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.logo-letter {
  font-weight: 800;
  background: linear-gradient(135deg, var(--tlh-primary), var(--tlh-secondary));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  transition: transform 0.2s ease, filter 0.2s ease;
}

.animated .logo-letter {
  animation: letter-pulse 3s ease-in-out infinite;
}

.logo-image {
  height: 100%;
  width: auto;
  object-fit: contain;
}

// Размеры
.logo-sm {
  .logo-letter { font-size: 1rem; }
  .logo-image { height: 24px; }
}

.logo-md {
  .logo-letter { font-size: 1.5rem; }
  .logo-image { height: 32px; }
}

.logo-lg {
  .logo-letter { font-size: 2rem; }
  .logo-image { height: 48px; }
}

.logo-xl {
  gap: 4px;
  .logo-letter { font-size: 3rem; }
  .logo-image { height: 64px; }
}

// Hover эффект
.org-logo:hover .logo-letter {
  filter: brightness(1.2);
}

@keyframes letter-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}
</style>
